/**
 * SodCalc — sq ft → slabs and pallets, with shape-based waste presets.
 *
 * Multi-zone input (front yard + back yard + side strips minus driveways).
 * Per research pain point #5, waste defaults differ by shape — straight
 * 5-10%, curved 10-15%. We expose three presets so contractors don't quote
 * everything at 5%.
 */

import React, { useMemo, useState } from 'react';
import type { Clave } from '../../../../i18n/textos';
import { useIdioma } from '../../../../i18n/idioma';
import toast from 'react-hot-toast';
import { Plus, X } from 'lucide-react';
import { CalculatorProps } from '../registry';
import { priceLine } from '../../../../utils/lineItemPricing';
import { NumberField, SelectField, OutputCard, OutputRow, AddToEstimateButton, SectionHeader } from '../CalculatorPrimitives';
import { Button } from '../../../ui/Button';
import type { EstimateItem } from '../../../../types';

type SodKey = 'bermuda-419' | 'tall-fescue' | 'kentucky-bluegrass' | 'st-augustine' | 'zoysia-emerald' | 'fescue-blend' | 'centipede';
type ShapeKey = 'rectangle' | 'curved' | 'complex';

interface SodOption {
  key: SodKey;
  name: string;
  catalogId: string;
  /** Sq ft per slab. Standard 16×24 slabs are 2.67 sq ft. */
  slabSqFt: number;
  /** Sq ft per pallet — varies by region but 450 is the most common default. */
  palletSqFt: number;
}

const SOD_OPTIONS: SodOption[] = [
  { key: 'bermuda-419',       name: 'Bermuda 419',           catalogId: 'landscaping.sod-bermuda-419-slab',         slabSqFt: 2.67, palletSqFt: 450 },
  { key: 'tall-fescue',       name: 'Tall Fescue',           catalogId: 'landscaping.sod-tall-fescue-slab',         slabSqFt: 2.67, palletSqFt: 500 },
  { key: 'kentucky-bluegrass',name: 'Kentucky Bluegrass',    catalogId: 'landscaping.sod-kentucky-bluegrass-slab',  slabSqFt: 2.67, palletSqFt: 504 },
  { key: 'st-augustine',      name: 'St. Augustine Floratam',catalogId: 'landscaping.sod-st-augustine-floratam-slab', slabSqFt: 2.67, palletSqFt: 450 },
  { key: 'zoysia-emerald',    name: 'Zoysia Emerald',        catalogId: 'landscaping.sod-zoysia-emerald-slab',      slabSqFt: 2.67, palletSqFt: 450 },
  { key: 'fescue-blend',      name: 'Fescue / Bluegrass blend', catalogId: 'landscaping.sod-fescue-bluegrass-blend-slab', slabSqFt: 2.67, palletSqFt: 500 },
  { key: 'centipede',         name: 'Centipede',             catalogId: 'landscaping.sod-centipede-slab',           slabSqFt: 2.67, palletSqFt: 450 },
];

const SOD_BY_KEY: Record<SodKey, SodOption> = Object.fromEntries(SOD_OPTIONS.map(s => [s.key, s])) as Record<SodKey, SodOption>;

const SHAPE_WASTE: Record<ShapeKey, number> = {
  rectangle: 5,
  curved: 10,
  complex: 15,
};

/* La llave es el dato del cálculo; solo la etiqueta es texto. */
const SHAPE_LABEL: Record<ShapeKey, Clave> = {
  rectangle: 'estimado.sod.forma.rectangular',
  curved: 'estimado.sod.forma.curveada',
  complex: 'estimado.sod.forma.complicada',
};

interface Zone {
  id: string;
  label: string;
  length: number;
  width: number;
}

const SodCalc: React.FC<CalculatorProps> = ({ onAddTemplateItems, pricebookByHint, targetMarginPercent, materialMarkupPercent }) => {
    const { t } = useIdioma();
  const [zones, setZones] = useState<Zone[]>([{ id: 'z1', label: t('estimados.calc.sod.zona_default'), length: 0, width: 0 }]);
  const [shape, setShape] = useState<ShapeKey>('rectangle');
  const [sodKey, setSodKey] = useState<SodKey>('bermuda-419');

  const sod = SOD_BY_KEY[sodKey];
  const wastePct = SHAPE_WASTE[shape];

  const calc = useMemo(() => {
    const totalSqFt = zones.reduce((sum, z) => sum + (z.length || 0) * (z.width || 0), 0);
    const sqFtWithWaste = totalSqFt * (1 + wastePct / 100);
    const slabs = sqFtWithWaste > 0 ? Math.ceil(sqFtWithWaste / sod.slabSqFt) : 0;
    const pallets = sqFtWithWaste > 0 ? Math.ceil(sqFtWithWaste / sod.palletSqFt) : 0;
    return { totalSqFt, sqFtWithWaste, slabs, pallets };
  }, [zones, wastePct, sod]);

  const canAdd = calc.slabs > 0;

  const handleAdd = () => {
    const cost = pricebookByHint.get(sod.catalogId) ?? 0;
    // El césped es MATERIAL: se cobra con el dial de MARKUP (sobre el COSTO),
    // no con el de margen. Con margen 30 / markup 20 la fórmula vieja daba un
    // 42,857% de markup y un 19% de sobreprecio sobre cada palet.
    const { rate, total, markupPercent, marginPercent } = priceLine('Material', cost, calc.slabs, {
      targetMarginPercent,
      materialMarkupPercent,
    });

    const item: EstimateItem = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'Material',
      description: `${sod.name} sod — ${calc.totalSqFt.toFixed(0)} sq ft`,
      quantity: calc.slabs,
      unit: 'slab',
      cost,
      rate,
      // El precio de la regla ES el precio base (k = 1). Sin estamparlo, una
      // fila agregada después de mover el dial de margen neto se quedaría una
      // generación atrás de otra idéntica agregada antes (Parte A.6.6).
      baseRate: rate,
      markupPercent,
      marginPercent,
      total,
      sourcePricebookId: sod.catalogId,
      calcBasis: `${calc.totalSqFt.toFixed(0)} sqft, +${wastePct}% waste (${shape}) → ${calc.pallets} pallet${calc.pallets === 1 ? '' : 's'}`,
      isOverridden: false,
    };
    onAddTemplateItems([item], { id: 'calc.sod', name: `Sod — ${calc.totalSqFt.toFixed(0)} sqft` });
    toast.success(`${t('estimados.calc.sod.toast_parte1')}${calc.slabs}${t('estimados.calc.sod.toast_parte2')}${sod.name.toLowerCase()}`);
  };

  const updateZone = (id: string, patch: Partial<Zone>) => {
    setZones(prev => prev.map(z => z.id === id ? { ...z, ...patch } : z));
  };
  const addZone = () => {
    setZones(prev => [...prev, { id: Math.random().toString(36).substr(2, 9), label: t('estimados.calc.sod.zona_nueva', { count: prev.length + 1 }), length: 0, width: 0 }]);
  };
  const removeZone = (id: string) => {
    setZones(prev => prev.length > 1 ? prev.filter(z => z.id !== id) : prev);
  };

  return (
    <>
      <SectionHeader title={t('estimados.calc.sod.areas')} />
      <div className="space-y-2">
        {zones.map(z => {
          const sqFt = (z.length || 0) * (z.width || 0);
          return (
            <div key={z.id} className="bg-acero-50 border border-acero-200 rounded-2xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={z.label}
                  onChange={(e) => updateZone(z.id, { label: e.target.value })}
                  placeholder={t('estimados.calc.sod.nombre_area')}
                  className="flex-1 h-8 px-2 text-[13px] font-medium text-navy-950 bg-transparent border border-transparent rounded-lg transition-[border-color,box-shadow] duration-150 focus:outline-none focus:bg-white focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                />
                {sqFt > 0 && <span className="text-[11px] font-data tabular-nums text-acero-600">{sqFt.toFixed(0)} sqft</span>}
                {zones.length > 1 && (
                  <button
                    onClick={() => removeZone(z.id)}
                    className="p-1 text-acero-400 hover:text-peligro hover:bg-peligro-bg rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    aria-label={t('estimados.calc.sod.quitar_area')}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumberField label={t('estimados.calc.sod.largo')} unit="ft" value={z.length} onChange={(n) => updateZone(z.id, { length: n })} />
                <NumberField label={t('estimados.calc.sod.ancho')} unit="ft" value={z.width} onChange={(n) => updateZone(z.id, { width: n })} />
              </div>
            </div>
          );
        })}
        <Button variant="outline" size="sm" className="w-full" onClick={addZone} leftIcon={<Plus size={13} />}>
          {t('estimados.calc.sod.agregar_area')}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SelectField
          label={t('estimados.calc.sod.forma')}
          value={shape}
          onChange={(v) => setShape(v)}
          options={(['rectangle', 'curved', 'complex'] as ShapeKey[]).map(k => ({ value: k, label: t(SHAPE_LABEL[k]) }))}
        />
        <SelectField
          label={t('estimados.calc.sod.tipo_pasto')}
          value={sodKey}
          onChange={(v) => setSodKey(v)}
          options={SOD_OPTIONS.map(s => ({ value: s.key, label: s.name }))}
        />
      </div>

      <SectionHeader title={t('estimados.calc.sod.necesitas')} />
      <OutputCard>
        <OutputRow label="Pallets" value={`${calc.pallets}`} emphasis="primary" hint={`${sod.palletSqFt} ${t('estimados.calc.sod.sqft_por_pallet')}`} />
        <OutputRow label="Slabs" value={`${calc.slabs}`} hint={`${sod.slabSqFt} ${t('estimados.calc.sod.sqft_por_slab')}`} />
        <OutputRow label={t('estimados.calc.sod.cobertura')} value={`${calc.totalSqFt.toFixed(0)} sq ft`} emphasis="muted" hint={`+${wastePct}${t('estimados.calc.sod.desperdicio')}${calc.sqFtWithWaste.toFixed(0)}${t('estimados.calc.sod.sqft_pedidos')}`} />
      </OutputCard>

      <AddToEstimateButton
        label={canAdd ? `Add ${calc.slabs} slabs to estimate` : t('estimados.calc.sod.boton_sin_medidas')}
        onClick={handleAdd}
        disabled={!canAdd}
      />
    </>
  );
};

export default SodCalc;
